/**
 * Tacit CRM API Client
 * Supabase CRUD operations for contact management
 */

import { supabase } from '@/lib/supabase';
import type { Tables } from '@/types/database';
import type { Contact, ContactFilters, AIProcessResponse, Activity } from './types.js';
import { sanitizeIlikeTerm } from './query-filters.js';
import { LANE_QUERY_SPEC, type ContactIndexRow, type DashboardLane } from './dashboard.js';

type CrmActivityRow = Tables<'crm_activities'>;

// The 6 columns `listContactIndex` selects — kept in one place so the
// `select()` string and the `ContactIndexRow` projection can never drift.
const CONTACT_INDEX_COLUMNS = 'id, status, action_state, due_date, last_contact_date, completed_at';

// ═══════════════════════════════════════════════════════════════════════════
// LIST CONTACTS OPTIONS
// ═══════════════════════════════════════════════════════════════════════════

export interface ListContactsFilters extends ContactFilters {
  limit?: number;
  offset?: number;
  orderBy?: { column: string; ascending?: boolean };
  /** Narrows to a single dashboard lane via `LANE_QUERY_SPEC` — a safe superset only, see dashboard.ts. */
  lane?: DashboardLane;
  /** Inclusive lower bound: `due_date >= dueFrom`. */
  dueFrom?: string;
  /** Inclusive upper bound: `due_date <= dueBefore`. */
  dueBefore?: string;
  /** Exact match: `due_date = dueOn`. */
  dueOn?: string;
  select?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// ALLOWED FIELDS (prevents PGRST204 errors from unknown fields)
// ═══════════════════════════════════════════════════════════════════════════

const ALLOWED_CONTACT_FIELDS = [
  'name', 'company', 'role', 'email', 'phone', 'linkedin_url',
  'location', 'timezone', 'company_size', 'decision_maker', 'preferred_contact_method',
  'status', 'pain_point', 'next_action', 'strategy',
  'action_state', 'action_state_changed_at',
  'due_date', 'completed_at', 'last_contact_date',
  'notes', 'raw_input', 'ai_metadata',
  'assigned_to',
  'deal_value', 'deal_currency', 'deal_closed_at',
  'external_contact_id', 'external_source',
];

// ═══════════════════════════════════════════════════════════════════════════
// ACTIVITY TYPE NORMALISATION
// ═══════════════════════════════════════════════════════════════════════════

// `crm_activities.activity_type` is a plain `string` column — the DB CHECK
// constraint and the `Activity['activity_type']` TS union have already
// drifted once elsewhere in this codebase (see src/lib/crm/labels.ts), so an
// unrecognised value must degrade to a safe fallback rather than being
// trusted as-is.
const KNOWN_ACTIVITY_TYPES: readonly string[] = [
  'email_sent', 'message_sent', 'meeting', 'call', 'note_added', 'status_changed',
];

function normalizeActivityType(value: string): Activity['activity_type'] {
  return KNOWN_ACTIVITY_TYPES.includes(value)
    ? (value as Activity['activity_type'])
    : 'note_added';
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPER: Get current user's organization
// ═══════════════════════════════════════════════════════════════════════════

async function getCurrentOrganizationId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) return null;
  
  // Get user's organization from organization_users
  const { data, error } = await supabase
    .from('organization_users')
    .select('organization_id')
    .eq('user_id', user.id)
    .limit(1)
    .single();
  
  if (error || !data) {
    console.error('Failed to get organization:', error);
    return null;
  }
  
  return data.organization_id;
}

// ═══════════════════════════════════════════════════════════════════════════
// CRM API CLIENT
// ═══════════════════════════════════════════════════════════════════════════

export const crmApi = {
  /**
   * List contacts with optional filters
   */
  async listContacts(
    organizationId: string,
    filters: ListContactsFilters = {},
  ): Promise<Contact[]> {
    let query = supabase
      .from('crm_contacts')
      .select(filters.select ?? '*')
      .eq('organization_id', organizationId);

    const orderBy = filters.orderBy ?? { column: 'due_date', ascending: true };
    query = query.order(orderBy.column, {
      ascending: orderBy.ascending ?? true,
      nullsFirst: false,
    });
    // Deterministic tie-break: `orderBy.column` (e.g. `due_date`) is not
    // unique — multiple contacts can share the same value (Task 3's own
    // tests deliberately create several on the same due date). Without a
    // secondary sort key, ties can come back in a different order across
    // separate paginated queries (especially under concurrent writes), so a
    // row can be skipped entirely or duplicated across pages. `id` is the
    // primary key, always unique, so it's a safe universal tie-break for
    // every paginated contact query — matches the `.order(x).order('id', …)`
    // convention already used elsewhere in this codebase
    // (`useReplyQueue.ts`, `useOutreach.ts`).
    if (orderBy.column !== 'id') {
      query = query.order('id', { ascending: true });
    }

    // Filter by status
    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    // Search by name or company. Routed through sanitizeIlikeTerm first —
    // PostgREST's `or(...)` grammar treats `,()` as structural separators,
    // so an unescaped occurrence in raw user input breaks the filter.
    if (filters.search) {
      const term = sanitizeIlikeTerm(filters.search);
      if (term) {
        const pattern = `%${term}%`;
        query = query.or(`name.ilike.${pattern},company.ilike.${pattern}`);
      }
    }

    // Filter overdue items (legacy boolean flag, unchanged)
    if (filters.overdue) {
      const today = new Date().toISOString().split('T')[0];
      query = query.lt('due_date', today).is('completed_at', null);
    }

    // Filter by action_state
    if (filters.action_state) {
      query = query.eq('action_state', filters.action_state);
    }

    // Lane narrowing. Derived mechanically from LANE_QUERY_SPEC so this can
    // never drift from dashboard.ts's own interpretation of the spec — see
    // the architectural invariant there. Single-value specs collapse to a
    // plain `.eq()`; the null-tolerant "my-turn" spec becomes an `.or()` of
    // positive equality/is-null clauses (never `not.in`).
    if (filters.lane) {
      const spec = LANE_QUERY_SPEC[filters.lane];
      const [singleStatus, ...restStatus] = spec.statusIn ?? [];

      if (singleStatus !== undefined && restStatus.length === 0) {
        query = query.eq('status', singleStatus);
      } else if (singleStatus !== undefined) {
        const statusIn = [singleStatus, ...restStatus];
        query = query.or(statusIn.map((value) => `status.eq.${value}`).join(','));
      }

      const actionStateIn = spec.actionStateIn ?? [];
      const actionStateClauses = actionStateIn.map((value) => `action_state.eq.${value}`);
      if (spec.allowNullActionState) actionStateClauses.push('action_state.is.null');

      const [singleActionStateClause, ...restActionStateClauses] = actionStateClauses;
      if (singleActionStateClause !== undefined && restActionStateClauses.length === 0) {
        if (spec.allowNullActionState && actionStateIn.length === 0) {
          query = query.is('action_state', null);
        } else {
          // Exactly one clause and it's not the null-only case, so
          // actionStateIn has exactly the one value this clause encodes.
          const [onlyActionState] = actionStateIn;
          if (onlyActionState !== undefined) query = query.eq('action_state', onlyActionState);
        }
      } else if (actionStateClauses.length > 1) {
        query = query.or(actionStateClauses.join(','));
      }
    }

    // due_date window: a safe, payload-reducing SUPERSET only — dashboard.ts
    // remains the sole authority on exact bucket membership. Comparison
    // operators against NULL are always false in Postgres, so these already
    // exclude null due_date rows; `.not(...)` below is a defensive, explicit
    // restatement of that (belt-and-braces, mirrors ContactFilters.overdue's
    // existing `.is('completed_at', null)` pairing above).
    if (filters.dueOn || filters.dueFrom || filters.dueBefore) {
      query = query.not('due_date', 'is', null);
    }
    if (filters.dueOn) {
      query = query.eq('due_date', filters.dueOn);
    } else {
      if (filters.dueFrom) query = query.gte('due_date', filters.dueFrom);
      if (filters.dueBefore) query = query.lte('due_date', filters.dueBefore);
    }

    // Pagination: range when both offset and limit are known, otherwise a
    // plain cap (e.g. a "today queue" that wants the top N with no offset).
    if (typeof filters.limit === 'number') {
      query = typeof filters.offset === 'number'
        ? query.range(filters.offset, filters.offset + filters.limit - 1)
        : query.limit(filters.limit);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    // `filters.select` is a runtime string, not a literal, so supabase-js
    // can't parse it at compile time for column-level type inference —
    // route through `unknown` rather than a direct (unsound) cast.
    return (data ?? []) as unknown as Contact[];
  },

  /**
   * Lightweight 6-column projection for dashboard counts/badges. Always
   * scoped to active (uncompleted) contacts and intentionally unbounded —
   * counts must reflect every active contact, not a page of them.
   */
  async listContactIndex(organizationId: string): Promise<ContactIndexRow[]> {
    const { data, error } = await supabase
      .from('crm_contacts')
      .select(CONTACT_INDEX_COLUMNS)
      .eq('organization_id', organizationId)
      .is('completed_at', null);

    if (error) {
      throw error;
    }

    return (data || []) as ContactIndexRow[];
  },

  /**
   * Get a single contact by ID
   */
  async getContact(id: string): Promise<Contact> {
    const { data, error } = await supabase
      .from('crm_contacts')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      console.error('Failed to get contact:', error);
      throw error;
    }
    
    return data as Contact;
  },

  /**
   * Create a new contact
   */
  async createContact(contact: Partial<Contact>): Promise<Contact> {
    const { data: { user } } = await supabase.auth.getUser();
    const organizationId = await getCurrentOrganizationId();
    
    if (!organizationId) {
      throw new Error('No organization found for user');
    }
    
    const { data, error } = await supabase
      .from('crm_contacts')
      .insert({
        ...contact,
        organization_id: organizationId,
        created_by: user?.id,
      })
      .select()
      .single();
    
    if (error) {
      console.error('Failed to create contact:', error);
      throw error;
    }
    
    return data as Contact;
  },

  /**
   * Update an existing contact
   */
  async updateContact(
    id: string,
    updates: Partial<Contact>,
    organizationId: string,
  ): Promise<Contact> {
    // Filter out any fields that don't exist in the database schema
    const sanitizedUpdates = Object.fromEntries(
      Object.entries(updates).filter(([key]) => ALLOWED_CONTACT_FIELDS.includes(key))
    );

    const { data, error } = await supabase
      .from('crm_contacts')
      .update(sanitizedUpdates)
      .eq('id', id)
      // Defence-in-depth alongside RLS: an update can never cross tenants
      // even if a caller ever passes a stale/mismatched id.
      .eq('organization_id', organizationId)
      .select()
      .single();

    if (error) {
      console.error('Failed to update contact:', error);
      throw error;
    }

    return data as Contact;
  },

  /**
   * Delete a contact
   */
  async deleteContact(id: string): Promise<void> {
    const { error } = await supabase
      .from('crm_contacts')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Failed to delete contact:', error);
      throw error;
    }
  },

  /**
   * Mark a contact as done
   */
  async markDone(id: string, organizationId: string): Promise<Contact> {
    return this.updateContact(id, {
      completed_at: new Date().toISOString(),
      action_state: 'archived',
    }, organizationId);
  },

  /**
   * Process raw text with AI to extract structured data
   */
  async processWithAI(rawText: string): Promise<AIProcessResponse> {
    const organizationId = await getCurrentOrganizationId();
    
    const response = await fetch('/api/crm/ai-process', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        raw_text: rawText,
        organization_id: organizationId 
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'AI processing failed');
    }
    
    const result = await response.json();
    return result.data as AIProcessResponse;
  },

  /**
   * Log an activity for a contact
   */
  async logActivity(
    contactId: string, 
    activityType: string, 
    description?: string, 
    metadata?: Record<string, any>
  ): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    const organizationId = await getCurrentOrganizationId();
    
    if (!organizationId) {
      throw new Error('No organization found for user');
    }
    
    const { error } = await supabase
      .from('crm_activities')
      .insert({
        contact_id: contactId,
        organization_id: organizationId,
        activity_type: activityType,
        description,
        metadata,
        created_by: user?.id,
      });
    
    if (error) {
      console.error('Failed to log activity:', error);
      throw error;
    }
  },

  /**
   * Get activities for a contact
   */
  async getActivities(organizationId: string, contactId: string): Promise<Activity[]> {
    const { data, error } = await supabase
      .from('crm_activities')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('contact_id', contactId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Failed to get activities:', error);
      throw error;
    }

    return ((data ?? []) as CrmActivityRow[]).map((row): Activity => ({
      id: row.id,
      contact_id: row.contact_id ?? contactId,
      organization_id: row.organization_id,
      activity_type: normalizeActivityType(row.activity_type),
      description: row.description ?? undefined,
      metadata: row.metadata == null ? undefined : (row.metadata as Record<string, unknown>),
      created_at: row.created_at ?? '',
      created_by: row.created_by ?? undefined,
    }));
  },
};
